
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

export const UserRole = ({ userId }: { userId: string }) => {
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId)
          .single();

        if (error) {
          console.error("Error fetching user role:", error);
          toast({
            title: "Error",
            description: "Could not fetch user role. Please try again later.",
            variant: "destructive",
          });
          setRole("user"); // Default to user role if error
        } else {
          setRole(data?.role || "user");
        }
      } catch (err) {
        console.error("Error in user role fetch:", err);
        setRole("user"); // Default to user role if error
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserRole();
    }
  }, [userId, toast]);

  // Helper function for determining badge variant based on role
  const getBadgeVariant = (role: string | null) => {
    switch (role) {
      case "admin":
        return "default";
      case "moderator":
        return "outline";
      case "editor":
        return "secondary";
      default:
        return "secondary";
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500">Loading role...</div>;
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-slate-700">Role:</span>
      <Badge
        variant={getBadgeVariant(role)}
        className="capitalize"
      >
        {role || "user"}
      </Badge>
    </div>
  );
};
